import React, { memo, useEffect, useRef } from 'react';
import { Common } from '../../utils/common';
import moment from 'moment';
import { momentDurationToTime } from './misc';

const $ = window.$;

/**
 * This component is a React wrapper around the [DataTables](https://datatables.net/) jQuery plugin.
 * It provides an API that allows you to declaratively define the table behavior and solves some
 * compatibility issues between React and DataTables.
 *
 * @example
 * <caption>Example usage of DataTable with basic column definitions and data.</caption>
 * ```jsx
 * <DataTable
 *   data={tableData}
 *   title='Example'
 *   columnDefs={[
 *     {
 *       data: 'column1',
 *       title: 'Column 1',
 *     },
 *     {
 *       data: 'column2',
 *       title: 'Column 2',
 *     },
 *   ]}
 * />
 * ```
 *
 * @example
 * <caption>Example usage of DataTable with aggregated columns and complex headers.</caption>
 * ```jsx
 * <DataTable
 *   data={tableData}
 *   showTotalFooter={true}
 *   title='Example'
 *   columnDefs={[
 *     {
 *       data: 'column1',
 *       title: 'Column 1',
 *       aggregation: 'count',
 *     },
 *     {
 *       data: 'column2',
 *       title: 'Column 2',
 *       aggregation: 'duration',
 *     },
 *     {
 *       title: 'Column Group',
 *       children: [
 *         {
 *           data: 'column3',
 *           title: 'Column 3',
 *           aggregation: 'sum',
 *         },
 *         {
 *           data: 'column4',
 *           title: 'Column 4',
 *           aggregation: 'sum',
 *         },
 *       ],
 *     },
 *   ]}
 * />
 * ```
 *
 * @param {Object} props
 * @param {ColumnDef[]} props.columnDefs - An array of column definitions for the table.
 *   Each item can be a [DataTables column object](https://datatables.net/reference/option/columns)
 *   or a column group object with a `title` property and a `children` property that is an array of
 *   DataTables column objects. The `title` property is required for either type.
 * @param {any[]} props.data - The array that will be used as the display data for the table.
 *   It will be passed to the DataTables `data` option and is documented
 *   [here](https://datatables.net/reference/option/data).
 * @param {string} props.title - The title of the table. Used for generating the file name of the
 *   exported excel data and for saving the state of the table in local storage.
 * @param {number[]} [props.hideColumnsFromExport=[]] - An optional array of column indexes to hide when exporting the excel data.
 * @param {boolean} [props.showTotalFooter=false] - Determines whether to show the "Total" footer.
 *   Set to `true` to show the footer.
 *   Use in combination with the `aggregation` property for a given column to aggregate that column.
 * @param {any[]} [props.order] - An optional array specifying the order of the columns.
 *   Documented [here](https://datatables.net/reference/option/order).
 * @param {boolean} [props.showCounterColumn=false] - Determines whether to show a counter column.
 * @param {boolean} [props.deferRender=false] - https://datatables.net/reference/option/deferRender
 * @param {boolean} [props.autoWidth=true] - https://datatables.net/reference/option/autoWidth
 * @param {string | Function} [props.rowId] - https://datatables.net/reference/option/rowId
 * @param {boolean} [props.selectColumns=true] - Determines whether to show the "Select Columns" button.
 * @param {boolean} [props.exportData=true] - Determines whether to show the "Export Data" button.
 * @param {Function} [props.createdRow] - https://datatables.net/reference/option/createdRow
 * @param {boolean} [props.stateSave=true] - https://datatables.net/reference/option/stateSave
 */
function DataTable({
  columnDefs: originalColumnDefs,
  data,
  title,
  hideColumnsFromExport = [],
  showTotalFooter = false,
  order,
  showCounterColumn = false,
  autoWidth = true,
  deferRender = false,
  rowId,
  selectColumns = true,
  exportData = true,
  createdRow,
  stateSave = true,
}) {
  const titleWithUnderscores = title.replace(/ /g, '_');
  const hasComplexHeaders = originalColumnDefs.some((column) => 'children' in column);

  // Clone the columnDefs array to avoid mutating the original array. Mutating the original array
  // will cause multiple counter columns to be added when the component is re-rendered when
  // `showCounterColumn` is true.
  const columnDefs = [...originalColumnDefs];

  // Add an empty column to the columnDefs array to be used for the counter column.
  if (showCounterColumn) {
    columnDefs.unshift({
      data: null,
      render() {
        return null;
      },
      orderable: false,
      searchable: false,
      title: '',
    });

    // Add the counter column to the list of columns to hide when exporting.
    hideColumnsFromExport.unshift(0);
  }

  // Flatten the columnDefs to an array of child columns to be passed to the DataTables `columns` option.
  const columns = columnDefs
    .map((column) => {
      if ('children' in column) {
        return column.children.map((child) => child);
      } else {
        return column;
      }
    })
    .flat();

  const firstAggregatedColumnIndex = columns.findIndex(
    (column) => column.aggregation !== undefined
  );

  const tableElement = useRef();
  useEffect(() => {
    const $element = $(tableElement.current);
    const dataTable = $element.DataTable({
      data,
      columns,
      order,
      autoWidth,
      deferRender,
      rowId,
      createdRow,
      stateSave,
      dom: '<fB>tlip',
      lengthMenu: [
        [10, 25, 50, 100, 200, -1],
        [10, 25, 50, 100, 200, 'All'],
      ],
      columnDefs: [{ className: 'text-center', targets: '_all' }],
      stateSaveCallback: (settings, data) => {
        localStorage.setItem(`DataTables-AI-${titleWithUnderscores}`, JSON.stringify(data));
      },
      stateLoadCallback: (settings) =>
        JSON.parse(localStorage.getItem(`DataTables-AI-${titleWithUnderscores}`)),
      buttons: [
        ...(selectColumns
          ? [
              {
                extend: 'colvis',
                text: 'Select Columns',
                collectionLayout: 'fixed two-column',
                columns: showCounterColumn ? ':gt(0)' : undefined,
              },
            ]
          : []),
        ...(exportData
          ? [
              {
                extend: 'excelHtml5',
                text: 'Export Data',
                title: titleWithUnderscores,
                exportOptions: {
                  columns(index) {
                    const isVisible = dataTable.column(index).visible();
                    if (!isVisible) {
                      return false;
                    } else if (hideColumnsFromExport.includes(index)) {
                      return false;
                    } else {
                      return true;
                    }
                  },
                  orthogonal: 'export',
                },
              },
            ]
          : []),
      ],
      destroy: true,
      responsive: true,
      footerCallback() {
        if (!showTotalFooter) {
          return;
        }
        const api = this.api();
        for (let i = firstAggregatedColumnIndex; i < columns.length; i++) {
          const column = columns[i];
          switch (column.aggregation) {
            case undefined:
              break;
            case 'sum': {
              const sum = api
                .column(i)
                .data()
                .reduce((a, b) => a + b, 0);
              $(api.column(i).footer()).html(Common.formatNumberWithCommas(sum));
              break;
            }
            case 'duration': {
              const sum = api
                .column(i)
                .data()
                .reduce((a, b) => a + b, 0);
              $(api.column(i).footer()).html(momentDurationToTime(moment.duration(sum, 'seconds')));
              break;
            }
            case 'count': {
              const totalCount = api.rows().count();
              $(api.column(i).footer()).html(totalCount);
              break;
            }
            default:
              throw new Error(`Unknown aggregation type: ${column.aggregation}`);
          }
        }
      },
      rowCallback(row, data, displayNum, displayIndex) {
        if (showCounterColumn) {
          $('td', row)
            .eq(0)
            .html(displayIndex + 1);
        }
      },
    });

    return () => {
      dataTable.destroy();
    };
  });

  return (
    <div
      // When the number of columns changes, this will cause a DataTables error because the number
      // of columns in the header and footer changes before the useEffect runs and updates the
      // number of columns in the body. Adding the column length as a key forces React to destroy
      // and remount the dom node when the column length changes, which causes DataTables to
      // reinitialize and fixes the error.
      key={columns.length}
      className='table-responsive'
    >
      <table
        ref={tableElement}
        className='table table-hover table-striped vertical_centered table-bordered'
        style={{ width: '100%' }}
      >
        {/* Only render a <thead> if there are complex headers. Otherwise DataTables takes care of this */}
        {hasComplexHeaders ? (
          <thead>
            <tr>
              {columnDefs.map((column, index) => {
                if ('children' in column) {
                  return (
                    <th
                      key={index}
                      className='text-center'
                      colSpan={column.children.length}
                      rowSpan={1}
                    >
                      {column.title}
                    </th>
                  );
                } else {
                  return (
                    <th key={index} colSpan={1} rowSpan={2}>
                      {column.title}
                    </th>
                  );
                }
              })}
            </tr>
            <tr>
              {columnDefs.map((column, columnIndex) => {
                if ('children' in column) {
                  return column.children.map((child, childIndex) => {
                    // If this is the last child column and the parent column is not the last column
                    // in the table, there will be no border on the right side of the column.
                    let style = undefined;
                    if (
                      childIndex === column.children.length - 1 &&
                      columnIndex !== columnDefs.length - 1
                    ) {
                      style = { borderRightWidth: 2 };
                    }
                    return (
                      <th key={child.title} colSpan={1} rowSpan={1} style={style}>
                        {child.title}
                      </th>
                    );
                  });
                } else {
                  return null;
                }
              })}
            </tr>
          </thead>
        ) : null}
        {showTotalFooter ? (
          <tfoot>
            <tr>
              <th colSpan={firstAggregatedColumnIndex} style={{ textAlign: 'right' }}>
                Total:
              </th>
              {columns.slice(firstAggregatedColumnIndex).map((column, index) => (
                <th key={index} className='text-center' />
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export default memo(DataTable);

/**
 * @typedef {Object} Column
 * @property {'sum' | 'duration' | 'count'} [aggregation] - Specifies the type of aggregation to be
 *   performed on the column. `showTotalFooter` must be set to `true` for the aggregation to be
 *   displayed in the footer. The following aggregation types are supported:
 *   - 'sum' - Adds the values of each column together.
 *   - 'duration' - Adds the total duration in seconds and displays it as a time duration.
 *   - 'count' - Adds the total number of rows for the column.
 * @property {string} [className] - https://datatables.net/reference/option/columns.className
 * @property {Function} [createdCell] - https://datatables.net/reference/option/columns.createdCell
 * @property {any} [data] - https://datatables.net/reference/option/columns.data
 * @property {string} [defaultContent] - https://datatables.net/reference/option/columns.defaultContent
 * @property {string} [name] - https://datatables.net/reference/option/columns.name
 * @property {boolean} [orderable] - https://datatables.net/reference/option/columns.orderable
 * @property {any} [render] - https://datatables.net/reference/option/columns.render
 * @property {boolean} [searchable] - https://datatables.net/reference/option/columns.searchable
 * @property {string} title - https://datatables.net/reference/option/columns.title
 * @property {string} [type] - https://datatables.net/reference/option/columns.type
 * @property {boolean} [visible] - https://datatables.net/reference/option/columns.visible
 * @property {string} [width] - https://datatables.net/reference/option/columns.width
 */

/**
 * @typedef {Object} ColumnGroup
 * @property {Column[]} children - An array of child column definitions that belong to this column group.
 * @property {string} title - The title of the column group. Will be displayed in the table header.
 */

/**
 * @typedef {Column | ColumnGroup} ColumnDef
 */
